/**
 * Ayuda de la inteligencia artificial con las imágenes del paciente: lectura asistida de radiografías (periapicales, panorámicas, aleta de mordida),
 * medición calibrada de la longitud aparente para endodoncia y simulación de aclaramiento dental en fotos.
 * La IA solo sugiere: lo que devuelve se valida aquí, el profesional acepta o descarta cada punto y nada pasa solo a la historia clínica.
 */
import { piezaValida } from "./odontograma.ts";

export const TIPOS_IMAGEN = ["periapical", "panoramica", "aleta"] as const;
export type TipoImagen = (typeof TIPOS_IMAGEN)[number];
export const NOMBRE_TIPO_IMAGEN: Record<TipoImagen, string> = { periapical: "Radiografía periapical", panoramica: "Radiografía panorámica", aleta: "Aleta de mordida" };

/** Qué puede señalar la IA y en qué tipo de radiografía tiene sentido buscarlo. */
export const HALLAZGOS_IMAGEN = {
  caries: { nombre: "Caries sospechosa", en: ["periapical", "panoramica", "aleta"] },
  caries_interproximal: { nombre: "Caries interproximal sospechosa", en: ["periapical", "panoramica", "aleta"] },
  camara_pulpar: { nombre: "Cámara pulpar (tamaño, calcificación o comunicación)", en: ["periapical", "aleta"] },
  conducto_radicular: { nombre: "Conductos radiculares (número, curvatura, calcificación)", en: ["periapical"] },
  longitud_aparente: { nombre: "Longitud aparente de la pieza", en: ["periapical"] },
  lesion_periapical: { nombre: "Imagen radiolúcida periapical sospechosa", en: ["periapical", "panoramica"] },
  perdida_osea: { nombre: "Pérdida ósea marginal", en: ["periapical", "panoramica", "aleta"] },
  calculo: { nombre: "Cálculo dental (imagen radiopaca en el margen)", en: ["periapical", "panoramica", "aleta"] },
  restauracion_defectuosa: { nombre: "Restauración con desajuste o recidiva sospechosa", en: ["periapical", "aleta", "panoramica"] },
  conducto_dentario_inferior: { nombre: "Recorrido del conducto dentario inferior", en: ["panoramica"] },
  agujero_mentoniano: { nombre: "Agujero mentoniano", en: ["panoramica"] },
  agujero_incisivo: { nombre: "Conducto o agujero nasopalatino (incisivo)", en: ["panoramica", "periapical"] },
  seno_maxilar: { nombre: "Seno maxilar (opacidad, engrosamiento o relación con las raíces)", en: ["panoramica", "periapical"] },
  pieza_incluida: { nombre: "Pieza incluida o retenida", en: ["panoramica", "periapical"] },
  otro: { nombre: "Otro hallazgo a revisar", en: ["periapical", "panoramica", "aleta"] },
} as const;
export type TipoHallazgoImagen = keyof typeof HALLAZGOS_IMAGEN;
export const CONFIANZAS = ["alta", "media", "baja"] as const;
export type Confianza = (typeof CONFIANZAS)[number];

export interface ZonaImagen { x: number; y: number; w: number; h: number }
/** Dos puntos de la imagen (coordenadas relativas 0 a 1): a = borde incisal o cúspide, b = ápice de la raíz. */
export interface MedidaImagen { a: { x: number; y: number }; b: { x: number; y: number } }
export interface HallazgoImagen { tipo: TipoHallazgoImagen; pieza: number | null; zona: ZonaImagen | null; descripcion: string; confianza: Confianza; /** solo en «longitud aparente» de una periapical */ medida?: MedidaImagen | null }
export interface LecturaImagen { calidad: "buena" | "regular" | "mala"; resumen: string; hallazgos: HallazgoImagen[]; limitaciones: string }

/** Tipo de radiografía según la vista con que se subió la foto; null si no es una radiografía que la IA pueda leer. */
export function tipoImagenDe(tipo: string, etiqueta: string): TipoImagen | null {
  if (tipo !== "radiografia") return null;
  const e = etiqueta.toLowerCase();
  if (/panor/.test(e)) return "panoramica";
  if (/aleta|bite/.test(e)) return "aleta";
  if (/periapical|inicial|conductometr|cono|obturaci|control|apertura/.test(e)) return "periapical";
  // «Otra radiografía», cefálica y similares: se lee como periapical solo si no es de cráneo
  if (/cef[aá]lic|craneo|cráneo|lateral/.test(e)) return null;
  return null;
}

const texto = (v: unknown, max: number) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim().slice(0, max) : "");
const unidad = (v: unknown) => { const n = Number(v); return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : null; };

/** Valida lo que devolvió la IA: solo tipos conocidos para esa radiografía, piezas válidas, zonas dentro de la imagen y textos acotados. */
/** Cuadrante FDI de una pieza (1 a 4 permanentes, 5 a 8 temporales): 5 → 1, 6 → 2, 7 → 3, 8 → 4. */
const cuadranteDe = (pieza: number) => { const q = Math.floor(pieza / 10); return q > 4 ? q - 4 : q; };

/**
 * Valida lo que devolvió la IA. Si el profesional indicó qué pieza(s) muestra la radiografía (`piezasIndicadas`), un hallazgo que la IA ubique en un
 * cuadrante distinto (por ejemplo, un molar inferior leído como superior) se deja sin pieza y se avisa en su descripción: la numeración indicada manda.
 */
export function normalizarLectura(crudo: unknown, tipoImagen: TipoImagen, piezasIndicadas: readonly number[] = []): LecturaImagen | null {
  if (!crudo || typeof crudo !== "object") return null;
  const o = crudo as Record<string, unknown>;
  const lista = Array.isArray(o.hallazgos) ? o.hallazgos : [];
  const hallazgos: HallazgoImagen[] = [];
  for (const h of lista.slice(0, 40)) {
    if (!h || typeof h !== "object") continue;
    const x = h as Record<string, unknown>;
    const tipo = String(x.tipo) as TipoHallazgoImagen;
    const def = HALLAZGOS_IMAGEN[tipo];
    if (!def || !(def.en as readonly string[]).includes(tipoImagen)) continue;
    const descripcion = texto(x.descripcion, 300);
    if (!descripcion) continue;
    const p = Number(x.pieza);
    let pieza = Number.isInteger(p) && piezaValida(p) ? p : null;
    let descripcionFinal = descripcion;
    if (pieza !== null && piezasIndicadas.length && !piezasIndicadas.some((q) => cuadranteDe(q) === cuadranteDe(pieza!))) {
      descripcionFinal = texto(`${descripcion} (La IA la ubicó en la pieza ${pieza}, que no corresponde a la zona indicada: se dejó sin pieza.)`, 300);
      pieza = null;
    }
    let zona: ZonaImagen | null = null;
    if (x.zona && typeof x.zona === "object") {
      const z = x.zona as Record<string, unknown>;
      const zx = unidad(z.x), zy = unidad(z.y), zw = unidad(z.w), zh = unidad(z.h);
      if (zx !== null && zy !== null && zw !== null && zh !== null) { const w = Math.min(zw, 1 - zx), h = Math.min(zh, 1 - zy); if (w >= 0.01 && h >= 0.01) zona = { x: zx, y: zy, w, h }; }
    }
    const confianza = (CONFIANZAS as readonly string[]).includes(String(x.confianza)) ? (x.confianza as Confianza) : "baja";
    // longitud aparente: dos puntos (corona y ápice) para que Donna calcule la medida; la IA no escribe milímetros
    let medida: MedidaImagen | null = null;
    if (tipo === "longitud_aparente" && x.medida && typeof x.medida === "object") {
      const m = x.medida as Record<string, Record<string, unknown> | undefined>;
      const ax = unidad(m.a?.x), ay = unidad(m.a?.y), bx = unidad(m.b?.x), by = unidad(m.b?.y);
      if (ax !== null && ay !== null && bx !== null && by !== null && Math.hypot(bx - ax, by - ay) >= 0.08) medida = { a: { x: ax, y: ay }, b: { x: bx, y: by } };
    }
    hallazgos.push({ tipo, pieza, zona, descripcion: descripcionFinal, confianza, ...(medida ? { medida } : {}) });
  }
  const resumen = texto(o.resumen, 800);
  const limitaciones = texto(o.limitaciones, 500);
  if (!resumen && !hallazgos.length && !limitaciones) return null;
  const calidad = o.calidad === "buena" || o.calidad === "mala" ? o.calidad : "regular";
  return { calidad, resumen, hallazgos, limitaciones };
}

/* ---------- longitud aparente para endodoncia (cálculo exacto, no lo hace la IA) ---------- */

/**
 * Pasa una medida en píxeles de la radiografía a milímetros con una referencia de longitud conocida (una lima con tope, un instrumento de largo
 * conocido o la longitud de una pieza ya medida). Es la longitud aparente: la imagen tiene distorsión y la longitud de trabajo se confirma con el localizador apical.
 */
export function longitudAparente(refMm: number, refPx: number, medidaPx: number): number | null {
  if (![refMm, refPx, medidaPx].every((n) => Number.isFinite(n)) || refMm <= 0 || refPx <= 0 || medidaPx <= 0 || refMm > 60) return null;
  const mm = (medidaPx * refMm) / refPx;
  return mm > 0 && mm < 60 ? Math.round(mm * 2) / 2 : null;
}
/**
 * Tamaños de película o sensor intraoral (milímetros, lado largo y lado corto). Si la imagen es el sensor completo (sin recortar), el lado largo
 * de la imagen mide lo mismo que el lado largo del sensor y de ahí sale la escala. Con recorte no sirve: se usa una referencia de largo conocido.
 */
export const SENSORES_PERIAPICALES: readonly { clave: string; nombre: string; largo: number; corto: number }[] = [
  { clave: "0", nombre: "Tamaño 0 (22 × 35 mm, niños)", largo: 35, corto: 22 },
  { clave: "1", nombre: "Tamaño 1 (24 × 40 mm, anteriores)", largo: 40, corto: 24 },
  { clave: "2", nombre: "Tamaño 2 (31 × 41 mm, estándar de adultos)", largo: 41, corto: 31 },
];
/** Milímetros por píxel si la imagen coincide con el sensor elegido (proporción parecida, con 8 % de margen); null si no coincide. */
export function escalaPorSensor(anchoPx: number, altoPx: number, clave: string): number | null {
  const s = SENSORES_PERIAPICALES.find((x) => x.clave === clave);
  if (!s || !(anchoPx > 0) || !(altoPx > 0)) return null;
  const largoPx = Math.max(anchoPx, altoPx), cortoPx = Math.min(anchoPx, altoPx);
  if (Math.abs(largoPx / cortoPx / (s.largo / s.corto) - 1) > 0.08) return null;
  return s.largo / largoPx;
}
/** Longitud aparente (mm, redondeada a 0,5) entre dos puntos relativos de una imagen de ese tamaño, con la escala dada. */
export function longitudPorPuntos(m: MedidaImagen, anchoPx: number, altoPx: number, mmPorPx: number): number | null {
  const px = Math.hypot((m.b.x - m.a.x) * anchoPx, (m.b.y - m.a.y) * altoPx);
  const mm = px * mmPorPx;
  return Number.isFinite(mm) && mm > 3 && mm < 60 ? Math.round(mm * 2) / 2 : null;
}
/** Distancia en píxeles entre dos puntos marcados sobre la imagen. */
export const distanciaPx = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(a.x - b.x, a.y - b.y);

/* ---------- simulación de aclaramiento (en el navegador, sin enviar la foto a nadie) ---------- */

/** Aclara un color de diente: sube la luminosidad y quita parte del amarillo (saturación). `nivel` va de 0 (sin cambio) a 1 (máximo orientativo). */
export function aclararRgb(r: number, g: number, b: number, nivel: number): [number, number, number] {
  const n = Math.min(1, Math.max(0, nivel));
  if (n === 0) return [r, g, b];
  const rf = r / 255, gf = g / 255, bf = b / 255;
  const mx = Math.max(rf, gf, bf), mn = Math.min(rf, gf, bf), d = mx - mn;
  let l = (mx + mn) / 2;
  let h = 0, s = 0;
  if (d > 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    h = mx === rf ? ((gf - bf) / d) % 6 : mx === gf ? (bf - rf) / d + 2 : (rf - gf) / d + 4;
    h *= 60; if (h < 0) h += 360;
  }
  s = s * (1 - 0.6 * n);
  l = l + (0.97 - l) * 0.55 * n * (l > 0.25 ? 1 : l / 0.25);
  const c = (1 - Math.abs(2 * l - 1)) * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = l - c / 2;
  const [r1, g1, b1] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return [Math.round((r1 + m) * 255), Math.round((g1 + m) * 255), Math.round((b1 + m) * 255)];
}

export const AVISO_IA_IMAGENES = "Ayuda de la inteligencia artificial: es una sugerencia para revisar, no un diagnóstico. El profesional examina la imagen, decide y registra lo que corresponde.";
export const AVISO_SIMULACION = "Simulación orientativa. El resultado real depende del paciente, del tratamiento y de la técnica; no es una garantía.";
